import { useEffect, useState } from 'react';
import axios from 'axios';
import { AuthStatus, useAuth } from '../auth';
import { DataGrid } from '@mui/x-data-grid';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';


import '../styles/documents.scss';



import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";



import NavBar from './navbar';
import Menu   from './menu';
import { buildSourceDocumentTemplate } from './documentTemplateRenderer';
import {
    buildLegalDocumentTemplate,
    determinadoTemplate,
    indeterminadoPruebaTemplate,
    indeterminadoCapacitacionTemplate,
    indeterminadoTemporadaTemplate,
    indeterminadoTemplate
} from './templates';


const endpoint = process.env.REACT_APP_ENDPOINT;

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDocumentDate = (value) => {
    if (!value) return '';
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatAddress = (address) => {
    if (!address) return '';
    return [
        address.calle,
        address.numero_exterior,
        address.numero_interior,
        address.colonia,
        address.ciudad,
        address.estado,
        address.codigo_postal
    ].filter(Boolean).join(', ');
};

const formatWorkSchedule = (schedule = {}) => {
    const weekDays = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const labels = {
        diurno: 'diurna',
        nocturno: 'nocturna',
        mixto: 'mixta',
        continua: 'continua',
        discontinua: 'discontinua',
        fijoDiasFijos: 'horario fijo en días fijos',
        variableDiasFijos: 'horario variable en días fijos',
        fijoDescansoVariable: 'horario fijo con días de descanso variables',
        variableDescansoVariable: 'horario variable con días de descanso variables'
    };
    const describeHours = (hours = {}) => {
        const periods = [];
        if (hours.inicio && hours.fin) periods.push(`${hours.inicio} a ${hours.fin}`);
        if (hours.inicio2 && hours.fin2) periods.push(`${hours.inicio2} a ${hours.fin2}`);
        return periods.join(' y ');
    };
    const describeDayRange = (start, end) => {
        const startIndex = weekDays.indexOf(start);
        const endIndex = weekDays.indexOf(end);
        if (startIndex < 0 || endIndex < 0) return '';
        const days = [];
        for (let index = startIndex, count = 0; count < weekDays.length; count += 1) {
            days.push(weekDays[index]);
            if (index === endIndex) break;
            index = (index + 1) % weekDays.length;
        }
        return days;
    };
    const mode = labels[schedule.tipoHorario] || schedule.tipoHorario;
    const shift = labels[schedule.turno] || schedule.turno;
    const dayType = labels[schedule.tipoJornada] || schedule.tipoJornada;
    const formatHours = (hours = {}) => {
        return describeHours(hours);
    };
    const lines = [];
    if (shift) lines.push(`Turno ${shift}`);
    if (mode) lines.push(mode);
    if (dayType) lines.push(`jornada ${dayType}`);

    if (['fijoDiasFijos', 'fijoDescansoVariable'].includes(schedule.tipoHorario) && schedule.horarioFijo) {
        const { desdeDia, hastaDia, horas } = schedule.horarioFijo;
        const days = describeDayRange(desdeDia, hastaDia);
        const dayText = days.length ? `días ${days.join(', ')}` : '';
        const hoursText = formatHours(horas);
        if (dayText || hoursText) lines.push([dayText, hoursText].filter(Boolean).join(': '));
    } else if (Array.isArray(schedule.horarioVariable) && schedule.horarioVariable.length) {
        const entries = schedule.horarioVariable
            .filter((row) => row.dia)
            .map((row) => `${row.dia}: ${formatHours(row.horas) || 'horario no especificado'}`);
        if (entries.length) lines.push(entries.join('; '));
    }

    if (schedule.diasDescanso !== undefined && schedule.diasDescanso !== null && schedule.diasDescanso !== '') {
        const count = Number(schedule.diasDescanso);
        if (Number.isFinite(count)) {
            lines.push(`${count} ${count === 1 ? 'día' : 'días'} de descanso semanal`);
        }
    }
    if (schedule.tiempoDescanso !== undefined && schedule.tiempoDescanso !== null && schedule.tiempoDescanso !== '') {
        lines.push(`${schedule.tiempoDescanso} minutos de descanso/alimentos por jornada`);
    }

    return lines.join(', ');
};

const getWeeklyRestDays = (schedule = {}) => {
    const weekDays = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const workingDays = [];

    if (schedule.tipoHorario === 'fijoDiasFijos' && schedule.horarioFijo) {
        const { desdeDia, hastaDia } = schedule.horarioFijo;
        let index = weekDays.indexOf(desdeDia);
        const endIndex = weekDays.indexOf(hastaDia);
        if (index >= 0 && endIndex >= 0) {
            for (let count = 0; count < weekDays.length; count += 1) {
                workingDays.push(weekDays[index]);
                if (index === endIndex) break;
                index = (index + 1) % weekDays.length;
            }
        }
    } else if (['variableDiasFijos', 'variableDescansoVariable'].includes(schedule.tipoHorario)) {
        (schedule.horarioVariable || []).forEach((row) => {
            if (row.dia && !workingDays.includes(row.dia)) workingDays.push(row.dia);
        });
    }

    if (workingDays.length) {
        return weekDays.filter((day) => !workingDays.includes(day)).join(', ');
    }
    if (['fijoDescansoVariable', 'variableDescansoVariable'].includes(schedule.tipoHorario)) {
        return 'variable';
    }
    return '';
};

const getWeeklyHours = (schedule = {}) => {
    const hoursPerDay = (hours = {}) => {
        const duration = (start, end) => {
            if (!start || !end) return 0;
            const [startHour, startMinute] = start.split(':').map(Number);
            const [endHour, endMinute] = end.split(':').map(Number);
            let total = (endHour + endMinute / 60) - (startHour + startMinute / 60);
            if (total < 0) total += 24;
            return total;
        };
        const total = duration(hours.inicio, hours.fin) + duration(hours.inicio2, hours.fin2);
        return Math.max(0, total - Number(schedule.tiempoDescanso || 0) / 60);
    };

    if (['fijoDiasFijos', 'fijoDescansoVariable'].includes(schedule.tipoHorario) && schedule.horarioFijo?.horas) {
        const daysWorked = Math.max(0, 7 - Number(schedule.diasDescanso || 0));
        return (hoursPerDay(schedule.horarioFijo.horas) * daysWorked).toFixed(2);
    }
    if (['variableDiasFijos', 'variableDescansoVariable'].includes(schedule.tipoHorario) && Array.isArray(schedule.horarioVariable)) {
        const total = schedule.horarioVariable
            .filter((row) => row.dia)
            .reduce((sum, row) => sum + hoursPerDay(row.horas), 0);
        return schedule.horarioVariable.some((row) => row.dia && (row.horas?.inicio || row.horas?.inicio2))
            ? total.toFixed(2)
            : '';
    }
    return '';
};


const Documents = ()=>{
    const status = AuthStatus();
    const navigate = useNavigate();
    const { token } = useAuth();


    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState({});
    const [collaborators, setCollaborators] = useState([]); 
    const [companyCollaborators, setCompanyCollaborators] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [selectedDocument, setDocument] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    const [selectedType, setSelectedType] = useState("");

    const contractTypes = [
        { value: "obra", label: "Contrato por obra determinada" },
        { value: "determinado", label: "Contrato por tiempo determinado" },
        { value: "indeterminado_capacitacion", label: "Contrato indeterminado con capacitación" },
        { value: "indeterminado_prueba", label: "Contrato indeterminado con prueba" },
        { value: "indeterminado_temporada", label: "Contrato indeterminado por temporada" },
        { value: "indeterminado", label: "Contrato por tiempo indeterminado" }
    ];
    const legalDocumentTypes = [
        { value: 'reglamento', label: 'Reglamento interior de trabajo' },
        { value: 'utilidades', label: 'Participación de utilidades' },
        { value: 'declaracion', label: 'Exhibición de declaración anual' },
        { value: 'proyecto', label: 'Proyecto de reparto de utilidades' },
        { value: 'seguridad', label: 'Seguridad e higiene' },
        { value: 'capacitacion', label: 'Capacitación y adiestramiento' },
        { value: 'antiguedad', label: 'Determinación de antigüedades' },
        { value: 'voluntaria', label: 'Renuncia voluntaria' },
        { value: 'embarazo', label: 'Renuncia de mujer embarazada' },
        { value: 'convenio', label: 'Convenio de terminación laboral' },
        { value: 'finiquito', label: 'Recibo de finiquito' },
        { value: 'programacion', label: 'Programación de vacaciones' },
        { value: 'disfrute', label: 'Disfrute de vacaciones' },
        { value: 'diferir', label: 'Solicitud de vacaciones' },
        { value: 'paternidad', label: 'Licencia por paternidad' },
        { value: 'anticipo', label: 'Anticipo de salario' }
    ];
    const allDocumentTypes = [...contractTypes, ...legalDocumentTypes];
    const legalDocumentRoutes = {
        reglamento: '/admin/mixts', utilidades: '/admin/mixts', declaracion: '/admin/mixts',
        proyecto: '/admin/mixts', seguridad: '/admin/mixts', capacitacion: '/admin/mixts',
        antiguedad: '/admin/mixts', voluntaria: '/admin/demmits', embarazo: '/admin/demmits',
        convenio: '/admin/demmits', finiquito: '/admin/demmits',
        programacion: '/admin/features', disfrute: '/admin/features',
        diferir: '/admin/features', paternidad: '/admin/features', anticipo: '/admin/features'
    };
    const contractTitles = {
        obra: 'CONTRATO INDIVIDUAL DE TRABAJO POR OBRA DETERMINADA',
        determinado: 'CONTRATO INDIVIDUAL DE TRABAJO POR TIEMPO DETERMINADO',
        indeterminado_capacitacion: 'CONTRATO INDIVIDUAL DE TRABAJO POR TIEMPO INDETERMINADO SUJETO A CAPACITACIÓN INICIAL',
        indeterminado_prueba: 'CONTRATO INDIVIDUAL DE TRABAJO POR TIEMPO INDETERMINADO SUJETO A PRUEBA',
        indeterminado_temporada: 'CONTRATO INDIVIDUAL DE TRABAJO POR TIEMPO INDETERMINADO SUJETO A TEMPORADA',
        indeterminado: 'CONTRATO INDIVIDUAL DE TRABAJO POR TIEMPO INDETERMINADO'
    };

    const populateTemplate = (contractData) => {
        const company = companies.find(c => Number(c.id) === Number(contractData.company_id)) || {};
        const collaborator = collaborators.find(c => Number(c.id) === Number(contractData.collaborator_id)) || {};
        const additional = contractData.additionalFields || {};
        const schedule = contractData.jornada_laboral || {};
        const fiscalAddress = company.addresses?.find(address => address.is_fiscal) || company.addresses?.[0];
        const fiscalAddressText = fiscalAddress
            ? [fiscalAddress.calle, fiscalAddress.numero_exterior, fiscalAddress.numero_interior,
                fiscalAddress.colonia, fiscalAddress.ciudad, fiscalAddress.estado, fiscalAddress.codigo_postal]
                .filter(Boolean).join(', ')
            : '';
        const workplaceAddress = [
            contractData.calle, contractData.numero_exterior, contractData.numero_interior,
            contractData.colonia, contractData.ciudad, contractData.estado, contractData.codigo_postal
        ].filter(Boolean).join(', ');
        const collaboratorName = `${collaborator.nombre || ''} ${collaborator.apellidos || ''}`.trim();
        const salary = Number(contractData.salario);
        const salaryText = Number.isFinite(salary)
            ? salary.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '';
        const daysOff = schedule.diasDescanso === undefined ? '' : schedule.diasDescanso;
        const scheduleText = formatWorkSchedule(schedule);
        const payments = Array.isArray(contractData.dias_pago) ? contractData.dias_pago.join(', ') : '';
        const beneficiaries = Array.isArray(collaborator.beneficiarios)
            ? collaborator.beneficiarios.map(value =>
                typeof value === 'string' ? value : Object.values(value || {}).filter(Boolean).join(' ')
            ).join(', ')
            : collaborator.beneficiarios;
        const birthDate = collaborator.fecha_nacimiento;
        const age = birthDate
            ? Math.max(0, Math.floor((Date.now() - new Date(birthDate).getTime()) / 31557600000))
            : '';

        // Mapeo de placeholders a datos del contrato
        var replacements = {
            // Información de la empresa
            '[patron]': company.nombre || 'NOMBRE DE LA EMPRESA',
            '[representante_legal]': company.representante_legal || 'REPRESENTANTE LEGAL',
            '[actividad]': company.giro_actividad || contractData.actividad || 'ACTIVIDAD DE LA EMPRESA',
            '[domicilio_fiscal]': fiscalAddressText || 'DOMICILIO FISCAL',
            '[rfc]': company.rfc || 'RFC EMPRESA',
            
            // Información del colaborador
            '[colaborador]': collaboratorName || contractData.nombre || 'NOMBRE DEL COLABORADOR',
            '[puesto]': contractData.puesto || 'PUESTO',
            '[sexo]': collaborator.sexo || 'SEXO',
            '[fecha_nacimiento]': formatDocumentDate(birthDate) || 'FECHA DE NACIMIENTO',
            '[identificacion]': collaborator.identificacion || contractData.identificacion || 'IDENTIFICACIÓN OFICIAL',
            '[domicilio_particular]': collaborator.domicilio || 'DOMICILIO PARTICULAR',
            '[rfc_trabajador]': collaborator.rfc || 'RFC TRABAJADOR',
            '[curp]': collaborator.curp || 'CURP',
            '[nss]': collaborator.nss || 'NÚMERO DE SEGURIDAD SOCIAL',
            '[nacionalidad]': collaborator.nacionalidad || 'NACIONALIDAD',
            '[edad]': age || 'EDAD',
            '[estado_civil]': collaborator.estado_civil || 'ESTADO CIVIL',
            '[email]': collaborator.email || 'EMAIL NO ESPECIFICADO',
            '[beneficiarios]': beneficiaries || 'BENEFICIARIOS NO ESPECIFICADOS',
            
            // Campos del contrato
            '[fecha_celebracion]': formatDocumentDate(contractData.fecha_celebracion) || new Date().toLocaleDateString('es-MX'),
            '[lugar_ciudad]': contractData.lugar_ciudad || 'CIUDAD',
            '[lugar_estado]': contractData.lugar_estado || 'ESTADO',
            '[fecha_generacion]': new Date().toLocaleDateString('es-MX'),
            
            // Campos específicos por tipo de contrato
            '[motivo]': additional.motivo || 'MOTIVO NO ESPECIFICADO',
            '[duracion]': additional.duracion || 'DURACIÓN NO ESPECIFICADA',
            '[cliente]': additional.cliente || 'CLIENTE NO ESPECIFICADO',
            '[descripcion_obra]': additional.descripcion_obra || 'DESCRIPCIÓN DE OBRA NO ESPECIFICADA',
            '[tipo_empleado]': additional.tipo_empleado === 'direccion'
                ? 'dirección/gerencia/profesionista/técnico'
                : additional.tipo_empleado || 'TIPO NO ESPECIFICADO',
            '[prueba]': additional.duracion_prueba || additional.duracion_capacitacion || 'DÍAS NO ESPECIFICADOS',
            '[razon]': additional.razon_temporada || 'RAZÓN NO ESPECIFICADA',
            '[temporada]': additional.duracion_temporada || 'DURACIÓN NO ESPECIFICADA',
            '[fin_temporada]': formatDocumentDate(additional.fin_temporada) || 'FECHA NO ESPECIFICADA',
            
            // Campos adicionales comunes
            '[lugar_trabajo]': workplaceAddress || 'LUGAR DE TRABAJO NO ESPECIFICADO',
            '[horario_trabajo]': scheduleText || 'HORARIO NO ESPECIFICADO',
            '[salario]': salaryText || 'SALARIO NO ESPECIFICADO',
            '[forma_pago]': 'transferencia electrónica de fondos',
            '[nombre_patron]': company.nombre || 'NOMBRE DEL PATRÓN',
            '[titulo_contrato]': contractTitles[contractData.tipo],
            '[descripcion_puesto]': contractData.actividad || 'ACTIVIDAD NO ESPECIFICADA',
            '[email_trabajador]': collaborator.email || 'EMAIL NO ESPECIFICADO',
            '[temporalidad_pago]': contractData.periodo_pago || 'PERIODO NO ESPECIFICADO',
            '[salario_numerico]': salaryText || 'SALARIO NO ESPECIFICADO',
            '[salario_letra]': salaryText || 'SALARIO NO ESPECIFICADO',
            '[dias_pago]': payments || 'DÍAS DE PAGO NO ESPECIFICADOS',
            '[tipo_jornada]': schedule.tipoJornada === 'continua' ? 'continua' : schedule.tipoJornada === 'discontinua' ? 'discontinua' : 'JORNADA NO ESPECIFICADA',
            '[horas_semanales]': schedule.horasSemanales
                ? `${schedule.horasSemanales} horas`
                : (getWeeklyHours(schedule) ? `${getWeeklyHours(schedule)} horas` : 'HORAS NO ESPECIFICADAS'),
            '[dias_descanso_semanal]': daysOff || 'DÍAS NO ESPECIFICADOS',
            '[dias_descanso_especificos]': getWeeklyRestDays(schedule) || 'NO ESPECIFICADOS',
            '[duracion_estimada]': additional.duracion || 'DURACIÓN NO ESPECIFICADA',
            '[dias_prueba]': additional.duracion_prueba || 'DÍAS NO ESPECIFICADOS',
            '[meses_capacitacion]': additional.duracion_capacitacion || 'MESES NO ESPECIFICADOS',
            '[razon_temporada]': additional.razon_temporada || 'RAZÓN NO ESPECIFICADA',
            '[duracion_temporada]': additional.duracion_temporada || 'DURACIÓN NO ESPECIFICADA'
        };
        let template;

        switch (contractData.tipo) {
            case 'obra':
                template = determinadoTemplate;
                break;
                
            case 'determinado':
                template = determinadoTemplate;
                break;
                
            case 'indeterminado_prueba':
                template = indeterminadoPruebaTemplate;
                break;
                
            case 'indeterminado_capacitacion':
                template = indeterminadoCapacitacionTemplate;
                break;
                
            case 'indeterminado_temporada':
                template = indeterminadoTemporadaTemplate;
                break;
                
            case 'indeterminado':
                template = indeterminadoTemplate;
                break;
                
            default:
                throw new Error(`Tipo de contrato no reconocido: ${contractData.tipo}`);
        }

        return template.replace(/\[([a-zA-Z0-9_]+)\]/g, (placeholder) =>
            escapeHtml(replacements[placeholder] || 'NO ESPECIFICADO')
        );
    };



    const handleExport = async(item) => {
       toast.info(item.isLegalDocument ? "Exportando documento..." : "Exportando contrato...");
  
        try {
            const company = companies.find((entry) => Number(entry.id) === Number(item.company_id));
            const collaborator = collaborators.find((entry) => Number(entry.id) === Number(item.collaborator_id));
            const template = buildSourceDocumentTemplate(item, company, collaborator, collaborators);
            if (!template) {
                throw new Error(`No existe una plantilla para el tipo "${item.tipo}".`);
            }

            // 1. Crear elemento temporal
            const tempDiv = document.createElement('div');
            tempDiv.style.position = 'fixed';
            tempDiv.style.left = '-9999px';
            tempDiv.style.top = '0';
            tempDiv.style.width = '210mm';
            tempDiv.style.background = 'white';
            document.body.appendChild(tempDiv);

            // 2. Poblar el template con los datos
            tempDiv.innerHTML = template;

            // 3. Esperar a que se renderice el contenido
            await new Promise(resolve => setTimeout(resolve, 100));

            // 4. Obtener la sección del template
            const templateSection = tempDiv.querySelector('#document-template');
            
            if (!templateSection) {
                throw new Error('No se pudo encontrar la sección del contrato');
            }

            // 5. Generar PDF
            const canvas = await html2canvas(templateSection, {
                scale: 1.5, // Reducir de 2 a 1.5 (suficiente para PDF)
                useCORS: true,
                backgroundColor: '#ffffff',
                width: templateSection.scrollWidth,
                height: templateSection.scrollHeight,
                logging: false,
                removeContainer: true
            });

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const pageMargin = 10;
            const contentWidth = pdfWidth - pageMargin * 2;
            const contentHeight = pdfHeight - pageMargin * 2;
            const ratio = contentWidth / canvas.width;
            const pageCanvasHeight = contentHeight / ratio;
            const templateBounds = templateSection.getBoundingClientRect();
            const canvasScale = canvas.width / templateBounds.width;
            const pageBreaks = Array.from(templateSection.children)
                .filter((element) => element.tagName !== 'STYLE')
                .map((element) => (element.getBoundingClientRect().bottom - templateBounds.top) * canvasScale)
                .filter((position) => position > 0 && position < canvas.height)
                .sort((left, right) => left - right);
            let sourceY = 0;
            let page = 0;
            while (sourceY < canvas.height) {
                const maxSourceEnd = Math.min(canvas.height, sourceY + pageCanvasHeight);
                const safeBreak = pageBreaks
                    .filter((position) => position > sourceY + 1 && position <= maxSourceEnd)
                    .pop();
                const sourceEnd = maxSourceEnd >= canvas.height
                    ? canvas.height
                    : Math.floor(safeBreak || maxSourceEnd);
                const sliceHeight = sourceEnd - sourceY;
                if (sliceHeight <= 0) {
                    throw new Error('No se pudo determinar el salto de página del documento');
                }
                if (page > 0) pdf.addPage();
                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = canvas.width;
                pageCanvas.height = sliceHeight;
                const pageContext = pageCanvas.getContext('2d');
                if (!pageContext) {
                    throw new Error('No se pudo preparar una página del PDF');
                }
                pageContext.drawImage(
                    canvas,
                    0,
                    sourceY,
                    canvas.width,
                    sliceHeight,
                    0,
                    0,
                    canvas.width,
                    sliceHeight
                );
                pdf.addImage(
                    pageCanvas.toDataURL('image/jpeg', 0.8),
                    'JPEG',
                    pageMargin,
                    pageMargin,
                    contentWidth,
                    sliceHeight * ratio
                );
                sourceY = sourceEnd;
                page += 1;
            }
            
            // 6. Configurar metadatos
            pdf.setProperties({
                title: `${allDocumentTypes.find((type) => type.value === item.tipo)?.label || item.tipo} - ${item.nombre}`,
                subject: allDocumentTypes.find((type) => type.value === item.tipo)?.label || item.tipo,
                author: 'Sistema de Contratos',
                creator: 'Avalco'
            });

            // 7. Descargar
            const fileName = `${item.tipo}_${item.nombre || 'documento'}`.replace(/[^a-zA-Z0-9_-]/g, '_') + '.pdf';
            pdf.save(fileName);

            // 8. Limpiar
            document.body.removeChild(tempDiv);
            
            toast.success("PDF exportado exitosamente");
            
        } catch (error) {
            console.error('Error exportando PDF:', error);
            toast.error("Error al exportar el PDF");
            
            // Limpiar en caso de error
            const tempElements = document.querySelectorAll('[style*="left: -9999px"]');
            tempElements.forEach(el => el.remove());
        }
    }

    const handleDelete = (item) => {
        if (!item.isLegalDocument) {
            toast.error('No se pudo identificar el tipo de documento a eliminar.');
            return;
        }
        if (!window.confirm('¿Eliminar este documento?')) return;
        axios.delete(`${endpoint}admin/documents/${item.id}`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(() => {
            toast.success('Documento eliminado.');
            fetchDocuments();
        }).catch(error => {
            toast.error(error.response?.data?.message || 'Error al eliminar el documento.');
        });
    }

    const columns = [
        
        { 
            field: 'tipo', 
            headerName:'Documento', 
            width: 300,
            valueGetter: (params) => {
                const tipo = params;
                const match = allDocumentTypes.find(ct => ct.value === tipo);
                return match ? match.label : tipo; 
            }
        },

        
      
        { 
            field: 'created_at', 
            headerName:'Fecha de creación', 
            width: 100,
            valueGetter: (params) => {
                const rawDate = params;
                if (!rawDate) return '';

                const fecha = new Date(rawDate);
                const dia = fecha.getDate();
                const mes = fecha.toLocaleString('es-MX', { month: 'long' });
                const año = fecha.getFullYear();

                return `${dia} de ${mes}, ${año}`;
            }
        },

        { 
            field: 'company_id', 
            headerName:'Patrón', 
            width: 150,
            valueGetter: (params) => {
                const id = params;
                const match = companies.find(ct => ct.id === id);
                return match ? match.nombre : id; 
            }
        },
       
        { field: 'nombre', headerName: 'Colaborador', width: 200 },

        {
            field: 'view',
            headerName: 'Acciones',
            width: 250,
            renderCell: (item) => (
                
                <>
                    
                    <button className='btn btn-primary' title='Exportar PDF' onClick={()=>handleExport(item.row)}>
                        <svg fill="#ffffff" width="18px" height="18px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <title>print</title> <path d="M5.656 6.938l-0.344 2.688h11.781l-0.344-2.688c0-0.813-0.656-1.438-1.469-1.438h-8.188c-0.813 0-1.438 0.625-1.438 1.438zM1.438 11.094h19.531c0.813 0 1.438 0.625 1.438 1.438v8.563c0 0.813-0.625 1.438-1.438 1.438h-2.656v3.969h-14.219v-3.969h-2.656c-0.813 0-1.438-0.625-1.438-1.438v-8.563c0-0.813 0.625-1.438 1.438-1.438zM16.875 25.063v-9.281h-11.344v9.281h11.344zM15.188 18.469h-8.125c-0.188 0-0.344-0.188-0.344-0.375v-0.438c0-0.188 0.156-0.344 0.344-0.344h8.125c0.188 0 0.375 0.156 0.375 0.344v0.438c0 0.188-0.188 0.375-0.375 0.375zM15.188 21.063h-8.125c-0.188 0-0.344-0.188-0.344-0.375v-0.438c0-0.188 0.156-0.344 0.344-0.344h8.125c0.188 0 0.375 0.156 0.375 0.344v0.438c0 0.188-0.188 0.375-0.375 0.375zM15.188 23.656h-8.125c-0.188 0-0.344-0.188-0.344-0.375v-0.438c0-0.188 0.156-0.344 0.344-0.344h8.125c0.188 0 0.375 0.156 0.375 0.344v0.438c0 0.188-0.188 0.375-0.375 0.375z"></path> </g></svg>
                    </button>
                    
                </>
            )
        }
               
    ];
    

    const handleChangeType = (event) => {
       
        setSelectedType(event.target.value);
      
    };

    const handleCreateDocument = () =>{
        if(selectedType == ''){
            toast.error("Selecciona el tipo")
        }else{
            const path = legalDocumentRoutes[selectedType] || '/admin/contract';
            navigate(path, { state: { new: selectedType } });
        }
    }


    const fetchLegacyContracts = async () => {
        try {
           
            const response = await axios.get(`${endpoint}admin/contracts/company/${selectedCompany.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log(response.data)
            setDocuments(response.data)
           
        } catch (error) {
            toast.error('Error al obtener documentos');
        }
    };

    const fetchDocuments = async () => {
        if (!selectedCompany?.id) return;
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const [contractsResponse, legalDocumentsResponse] = await Promise.all([
                axios.get(`${endpoint}admin/contracts/company/${selectedCompany.id}`, config),
                axios.get(`${endpoint}admin/documents/company/${selectedCompany.id}`, config)
            ]);
            const legalDocuments = legalDocumentsResponse.data.map(document => {
                const collaborator = collaborators.find(item => item.id === document.collaborator_id);
                const type = legalDocumentTypes.find(item => item.value === document.tipo);
                return {
                    ...document,
                    rowId: `legal-${document.id}`,
                    isLegalDocument: true,
                    nombre: collaborator
                        ? `${collaborator.nombre} ${collaborator.apellidos}`
                        : type?.label || document.tipo
                };
            });
            setDocuments([...contractsResponse.data, ...legalDocuments]);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al obtener documentos.');
        }
    };


    const fetchCompanies = async () => {
        try {
            const response = await axios.get(`${endpoint}admin/companies`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setCompanies(response.data);
            console.log(response.data)
            if(response.data.length > 0){
                // verificar que todas las companies tengan un address 

                let allHaveAddress = true;
                for (const company of response.data) {
                    if (!company.addresses || company.addresses.length === 0 ) {    
                        allHaveAddress = false;
                        break;
                    }   
                }
                if (!allHaveAddress) {
                    toast.warn("Todas las empresas deben tener una dirección registrada. Actualiza la información para continuar.");
                    setTimeout(()=>{
                        navigate('/admin'); 
                    },5000);
                    return; // Salir de la función si alguna empresa no tiene dirección
                }

                setSelectedCompany(response.data[0]);
            }else{
                toast.warn("No tienes ninguna empresa registrada. Crea una para continuar.")
                setTimeout(()=>{
                    navigate('/admin'); 
                },4000);
            }
        } catch (error) {
            toast.error('Error al obtener la lista de companias')
        }
    };

    const fetchCollaborators = async () => {
        try {
            const response = await axios.get(`${endpoint}admin/collaborators`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            
            setCollaborators(response.data);
            
            if(selectedCompany.id)
                setCompanyCollaborators(response.data.filter(collaborator=>collaborator.company_id === selectedCompany.id))
            
            //console.log(response.data)
        } catch (error) {
            toast.error('Error al obtener la lista de colaboradores')
        }
    };
    

    useEffect(()=>{
       
        fetchDocuments();

    },[selectedCompany]);

    useEffect(()=>{
        fetchCompanies();
        fetchCollaborators();
    },[]);


    return(
        <div className="documents">
            <NavBar auth={status} companies={companies} selected={selectedCompany} set={setSelectedCompany}/>
            <div className='documents-row'>
                <Menu/>
                <div className='documents-wrapper'>
                    <ToastContainer />
                    <h2>Documentos</h2>

                    <div className="documents-controls">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar documento..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />

                        <span>
                            
                        
                   
                            <button type="button" class="btn btn-primary" data-bs-toggle="modal" data-bs-target="#createModal">
                                Agregar documento
                            </button>
                          
                         

                        </span>
                        
                    </div>

                    <div style={{ height: '80vh', width: '100%' }} className='documents-table'>
                        <DataGrid
                            rows={documents}
                            getRowId={(row) => row.rowId || row.id}
                            columns={columns}
                            sx={{ borderColor: 'white' }}
                            pageSize={5}
                            //onRowDoubleClick={()=>{document.getElementById('view').click(); setForm();}}
                            onRowClick={(row) => {setDocument(row.row);}}
                            
                        />
                    </div>
                    
                </div>
            </div>

    
            <div class="modal fade" id="createModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
                <div class="modal-dialog">
                    <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="exampleModalLabel">Crear documento</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <label htmlFor="">Tipo de documento</label>
                        <select 
                            id="contract-type"
                            className="form-control"
                            value={selectedType}
                            onChange={handleChangeType}
                        >
                            <option value="">Elige una opción</option>
                            {allDocumentTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div class="modal-footer">
                       
                        <button type="button" class="btn btn-primary" data-bs-dismiss="modal" onClick={handleCreateDocument}>Continuar</button>
                    </div>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default Documents;